import React from "react";
import { SegmentedControlContainer } from "./StyledComponents";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

interface SegmentedControlProps {
  options: string[];
  top: string;
  is100?: boolean;
}

const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  top,
  is100,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedIndex = options.findIndex((tab) =>
    location.pathname.endsWith(`/${tab}`)
  );
  const basePath = React.useMemo(() => {
    const pattern = new RegExp(`/(${options.join("|")})$`);
    return location.pathname.replace(pattern, "");
  }, [location.pathname, options]);

  return (
    <SegmentedControlContainer
      p={"2px"}
      display="flex"
      alignItems="center"
      sx={{
        position: "absolute",
        top: {
          xs: parseFloat(top) - 2 + "%",
          sm: parseFloat(top) - 4 + "%",
          md: parseFloat(top) - 2 + "%",
          lg: top,
        },
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 2,
        maxWidth: 1200,
        width: is100 ? "100%" : { xs: "90%", md: "90%" },
        flexWrap: "wrap", // Allow buttons to wrap to the next line if needed
        justifyContent: "center", // Center the buttons within the container
        backgroundColor: "rgba(250, 248, 243, 0.86) !important",
        backdropFilter: "saturate(180%) blur(22px) !important",
        border: "1px solid rgba(228, 222, 210, 0.78)",
        boxShadow: "0 12px 30px rgba(32,36,42,0.12)",
        "@media (max-width: 900px)": {
          backgroundColor: "rgba(250, 248, 243, 0.94) !important",
          backdropFilter: "saturate(160%) blur(16px) !important",
        },
      }}
    >
      {options.map((option, index) => (
        <Button
          key={index}
          onClick={() => {
            const target = `${basePath}/${option}`;
            if (location.pathname !== target) {
              navigate(target, { replace: true });
            }
          }}
          style={{
            flex: "1 1 auto", // Allow flexible sizing
            borderRadius: "10px",
            border: "none",
            margin: "3px",
            cursor: "pointer",
            outline: "none",
            transition: "background-color 0.3s, color 0.3s",
            backgroundColor:
              selectedIndex === index ? "#C46A2D" : "transparent",
            color: "#20242A",
            textTransform: "capitalize",
            fontWeight: 400,
            fontFamily: "Source Sans Pro Regular",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          sx={{
            fontSize: { xs: 13, sm: 14, md: 15 },
            py: "10px",
            px: { xs: "10px", sm: "45px" },
          }} // Ensure minimum button width
        >
          {t(option)}
        </Button>
      ))}
    </SegmentedControlContainer>
  );
};

export default SegmentedControl;
